// ABOUTME: Main layout component with sidebar based on new design.
// ABOUTME: Sidebar contains logo, status cards, language selector and logout.

import React, { useState, useEffect } from 'react';
import { Box, Select, MenuItem, FormControl, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { useTranslation } from 'react-i18next';
import StatusBar from './StatusBar';
import { useShop } from '../contexts/ShopContext';
import { colors } from '../theme/colors';
import { format } from 'date-fns';
import {
  ArrowRightStartOnRectangleIcon,
  ClockIcon,
  Cog6ToothIcon,
} from './icons/HeroIcons';

const SIDEBAR_WIDTH = 270;

function Layout({ children }) {
  const { logout } = useAuth();
  const { dashboardState } = useDashboard();
  const { currentShop } = useShop();
  const { t, i18n } = useTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: colors.background.sidebar }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
        }}
      >
        {/* Logo / App Name */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2,
            py: 1.5,
            bgcolor: colors.background.sidebar,
          }}
        >
          <Box
            sx={{
              fontSize: '1.125rem',
              fontWeight: 700,
              color: '#1f2937',
              letterSpacing: '-0.025em',
            }}
          >
            Lipóti Pékség AI
          </Box>
          {currentShop && (
            <Box
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#6b7280',
                mt: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              {currentShop.name || currentShop.shop_id}
              {currentShop.virtual && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: '#1d4ed8',
                    bgcolor: '#dbeafe',
                    borderRadius: '9999px',
                    px: 1,
                    py: 0.25,
                  }}
                >
                  {t('common.virtualShop')}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Status Cards area - gray background */}
        <Box
          sx={{
            flexGrow: 1,
            bgcolor: colors.background.sidebar,
            p: 2,
            pt: dashboardState.dashboardData ? 5.5 :  6.5,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Status Cards */}
          <Box sx={{ flexGrow: 1 }}>
            {dashboardState.dashboardData ? (
              <StatusBar
                currentTime={dashboardState.currentTime}
                dashboardData={dashboardState.dashboardData}
                onReloadConfig={dashboardState.onReloadConfig}
              />
            ) : (
              /* Show just current time and config button when no dashboard data */
              <>
                <Box
                  sx={{
                    bgcolor: 'white',
                    borderRadius: '10px',
                    border: `1px solid ${colors.border.light}`,
                    p: 1.5,
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClockIcon width={28} height={28} style={{ color: colors.ui.button }} />
                    <Box sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#525252' }}>
                      {t('dashboard.currentTime')}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
                      {format(currentTime, 'HH:mm:ss')}
                    </Box>
                  </Box>
                </Box>
                <Button
                  onClick={() => { window.location.href = '/config'; }}
                  startIcon={<Cog6ToothIcon width={20} height={20} style={{ color: 'white' }} />}
                  fullWidth
                  sx={{
                    bgcolor: colors.ui.button,
                    color: 'white',
                    textTransform: 'none',
                    borderRadius: '8px',
                    py: 1,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    '&:hover': { bgcolor: colors.ui.buttonHover },
                  }}
                >
                  {t('config.title')}
                </Button>
              </>
            )}
          </Box>

          {/* Bottom actions: Logout and Language selector */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1.5,
              pt: 2,
              borderTop: `1px solid ${colors.border.light}`,
            }}
          >
            <Button
              onClick={handleLogout}
              startIcon={
                <ArrowRightStartOnRectangleIcon width={20} height={20} style={{ color: 'white' }} />
              }
              sx={{
                flexGrow: 1,
                bgcolor: colors.ui.button,
                color: 'white',
                textTransform: 'none',
                borderRadius: '8px',
                px: 2,
                py: 1,
                fontWeight: 500,
                '&:hover': { bgcolor: colors.ui.buttonHover },
              }}
            >
              {t('common.logout')}
            </Button>

            <FormControl size="small">
              <Select
                value={i18n.language.toUpperCase()}
                onChange={(e) => i18n.changeLanguage(e.target.value.toLowerCase())}
                displayEmpty
                IconComponent={() => null}
                sx={{
                  bgcolor: 'white',
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: '8px',
                  minWidth: 60,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    py: 1,
                    px: 2,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      mt: 0.5,
                    },
                  },
                }}
              >
                <MenuItem value="HU" sx={{ fontWeight: 500, justifyContent: 'center' }}>HU</MenuItem>
                <MenuItem value="EN" sx={{ fontWeight: 500, justifyContent: 'center' }}>EN</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
          borderTopLeftRadius: '24px',
          borderBottomLeftRadius: '24px',
        }}
      >
        {/* Page Content */}
        <Box sx={{ p: 3, flexGrow: 1 }}>{children}</Box>
      </Box>
    </Box>
  );
}

export default Layout;
